import * as React from 'react'
import { Editor } from '@tiptap/react'
import { Trash2, Globe } from 'lucide-react'
import { Button } from '@/tiptap/tiptap-ui-primitive/button'
import { DocIcon } from '@/components/ui/doc-icon'
import './link-editor.scss'

interface LinkEditFormProps {
  url: string
  onSave: (url: string, title?: string) => void
  onRemove: () => void
  editor: Editor
  initialTitle?: string
  onTitleChange?: (title: string) => void
}

export function LinkEditForm({ url, onSave, onRemove, editor, initialTitle, onTitleChange }: LinkEditFormProps) {
  const [linkUrl, setLinkUrl] = React.useState(url)
  const [linkTitle, setLinkTitle] = React.useState(initialTitle || '')
  const urlInputRef = React.useRef<HTMLInputElement>(null)
  const updateTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)
  
  // The Topic model is gone; the internal-page-link picker has nothing to offer.
  const allTopicsFlat: any[] = []
  
  // Find linked topic if internal link
  const linkedTopic = React.useMemo(() => {
    if (!linkUrl) return null
    
    const topicMatch = linkUrl.match(/^\/topics\/(.+)/)
    if (topicMatch) {
      const topicId = topicMatch[1]
      return allTopicsFlat.find(topic => topic.id === topicId)
    }
    
    return null
  }, [linkUrl, allTopicsFlat])
  
  const displayUrl = linkedTopic?.title || linkUrl
  const isInternal = !!linkedTopic
  
  // Get the selected text to use as default title
  React.useEffect(() => {
    if (!linkTitle && !initialTitle) {
      const { from, to } = editor.state.selection
      const selectedText = editor.state.doc.textBetween(from, to, ' ')
      if (selectedText && selectedText !== linkUrl) {
        setLinkTitle(selectedText)
      }
    }
  }, [editor, linkTitle, initialTitle, linkUrl])
  
  React.useEffect(() => {
    // Update linkUrl when url prop changes
    setLinkUrl(url)
  }, [url])
  
  React.useEffect(() => {
    // Focus URL input on mount and select all text
    if (urlInputRef.current && !isInternal) {
      urlInputRef.current.focus()
      urlInputRef.current.select()
    }
  }, [])
  
  // Real-time update with debouncing
  React.useEffect(() => {
    if (onTitleChange && linkTitle !== initialTitle) {
      // Clear existing timeout
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current)
      }
      
      // Set new timeout for real-time update
      updateTimeoutRef.current = setTimeout(() => {
        onTitleChange(linkTitle)
      }, 100) // Reduced to 100ms for smoother updates
    }
    
    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current)
      }
    }
  }, [linkTitle, initialTitle, onTitleChange])
  
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleSave()
    }
  }
  
  const handleSave = () => {
    if (linkUrl) {
      // Always save the actual URL, not the display value
      onSave(linkUrl, linkTitle || undefined)
    }
  }
  
  return (
    <div className="link-editor">
      <div className="link-editor-field">
        <label className="link-editor-label">Page or URL</label>
        <div className="link-editor-url-display">
          <div className="link-editor-url-icon">
            {isInternal && linkedTopic ? (
              <DocIcon topic={linkedTopic} />
            ) : (
              <Globe className="h-4 w-4" />
            )}
          </div>
          <input
            ref={urlInputRef}
            value={displayUrl}
            onChange={(e) => {
              // If it's an internal link, don't allow direct editing
              if (!isInternal) {
                setLinkUrl(e.target.value)
              }
            }}
            onKeyDown={handleKeyDown}
            onFocus={(e) => e.target.select()}
            placeholder="Paste or type a link..."
            className="link-editor-input link-editor-input--with-icon"
            type="text"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            readOnly={isInternal}
            style={{ cursor: isInternal ? 'not-allowed' : 'text' }}
          />
        </div>
      </div>
      
      <div className="link-editor-field">
        <label className="link-editor-label">Title</label>
        <input
          value={linkTitle}
          onChange={(e) => setLinkTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={(e) => e.target.select()}
          placeholder="Text to display"
          className="link-editor-input"
          type="text"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
        />
      </div>
      
      <div className="link-editor-footer">
        <Button
          onClick={onRemove}
          className="link-editor-remove-button"
        >
          <Trash2 className="h-4 w-4" />
          Remove link
        </Button>
      </div>
    </div>
  )
}